import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';

function AnimatedCounter({ end, suffix, duration = 2 }: { end: number; suffix: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px 0px -50px 0px' });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const step = end / (duration * 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [isInView, end, duration]);

  return (
    <span ref={ref} className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
      {count}{suffix}
    </span>
  );
}

export default function Stats() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-80px 0px -80px 0px' });

  const stats = [
    { end: 384, suffix: '+', labelKey: 'stats.businesses', icon: 'ri-building-line' },
    { end: 95, suffix: '%', labelKey: 'stats.satisfaction', icon: 'ri-emotion-happy-line' },
    { end: 45, suffix: 'M+', labelKey: 'stats.revenue', icon: 'ri-money-dollar-circle-line' },
    { end: 10, suffix: '+', labelKey: 'stats.experience', icon: 'ri-time-line' },
  ];

  return (
    <section className="relative z-20 -mt-14 md:-mt-20 pb-12 md:pb-20" ref={sectionRef}>
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <motion.div
          className="bg-white/85 backdrop-blur-xl rounded-2xl border border-gray-100 p-8 md:p-10"
          style={{
            boxShadow: '0 4px 24px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.03)',
          }}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.labelKey}
                className="text-center group"
                initial={{ opacity: 0, y: 15 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.15 + i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="w-11 h-11 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-400">
                  <i className={`${stat.icon} text-base text-primary-500 group-hover:text-white transition-colors duration-400`}></i>
                </div>
                <AnimatedCounter end={stat.end} suffix={stat.suffix} />
                <p className="text-gray-500 text-sm mt-1.5 font-medium">{t(stat.labelKey)}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}